import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Eye, FilePlus2, Pencil, Pin, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { WorkItemPicker } from '@/components/work/WorkItemPicker';
import { WorkInstructionsPanel } from '@/components/work/WorkInstructionsPanel';
import { QuietEmpty } from '../shared';
import { useSaveInstruction, useWorkInstructions } from '@/features/workMode/hooks';
import { useStationItemIds } from '@/features/workMode/stationSetup';
import { describeWorkError, formatDuration } from '@/features/workMode/api';
import { instructionEditorHref } from '@/features/workMode/links';
import { formatRelative } from '@/lib/formatters';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { FloorWorkstation, WorkInstructionWithSteps } from '@/features/workMode/types';

interface StationInstructionsTabProps {
  station: FloorWorkstation;
  lookups: WorkLookups;
  canManage: boolean;
}

/**
 * The work instructions a worker sees at this bench, and the gaps. A set
 * pinned to the station always wins; otherwise the generic published set
 * for each item built here applies (the same rule the tablet and the
 * sign-off copy use). Items built here with nothing published are called
 * out so the gap gets closed from this page.
 */
export function StationInstructionsTab({
  station,
  lookups,
  canManage,
}: StationInstructionsTabProps) {
  const { data: sets = [], isLoading } = useWorkInstructions();
  const { data: linkedItemIds = [] } = useStationItemIds(station.id);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<WorkInstructionWithSteps | null>(null);

  const itemIds = useMemo(() => {
    const ids = new Set(linkedItemIds);
    if (station.default_item_id) ids.add(station.default_item_id);
    return [...ids];
  }, [linkedItemIds, station.default_item_id]);

  const model = useMemo(() => {
    const pinned = sets.filter((s) => s.workstation_id === station.id);
    const pinnedPublishedItems = new Set(
      pinned.filter((s) => s.is_published).map((s) => s.item_id),
    );
    const generic = sets.filter(
      (s) =>
        s.workstation_id === null &&
        itemIds.includes(s.item_id) &&
        !pinnedPublishedItems.has(s.item_id),
    );
    const covered = new Set(
      [...pinned.filter((s) => s.is_published), ...generic.filter((s) => s.is_published)].map(
        (s) => s.item_id,
      ),
    );
    const missing = itemIds.filter((id) => !covered.has(id));
    const byItem = (a: WorkInstructionWithSteps, b: WorkInstructionWithSteps) =>
      (lookups.itemName(a.item_id) ?? '').localeCompare(lookups.itemName(b.item_id) ?? '') ||
      a.title.localeCompare(b.title);
    return { pinned: pinned.sort(byItem), generic: generic.sort(byItem), missing };
  }, [sets, station.id, itemIds, lookups]);

  const live =
    model.pinned.filter((s) => s.is_published).length +
    model.generic.filter((s) => s.is_published).length;

  if (isLoading) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {live === 0
            ? 'Nothing is live on this bench yet.'
            : `${live} set${live === 1 ? '' : 's'} live on the tablet here.`}
          {model.missing.length > 0 && (
            <span className="text-fresh-salmon-dark">
              {' '}
              {model.missing.length} item{model.missing.length === 1 ? '' : 's'} built here with no
              instructions.
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/mfg/workstations?tab=instructions">
              <BookOpen className="h-4 w-4" />
              All instructions
            </Link>
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <FilePlus2 className="h-4 w-4" />
              New set for this station
            </Button>
          )}
        </div>
      </div>

      {model.missing.length > 0 && (
        <section className="rounded-2xl border border-fresh-salmon/60 bg-fresh-salmon/10 p-4">
          <div className="flex items-start gap-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-fresh-salmon-dark" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                Built here, no published instructions
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {model.missing.map((id) => (
                  <li key={id}>
                    <MissingItemChip
                      itemId={id}
                      lookups={lookups}
                      station={station}
                      canManage={canManage}
                      draft={
                        sets.find(
                          (s) =>
                            s.item_id === id &&
                            (s.workstation_id === station.id || s.workstation_id === null) &&
                            !s.is_published,
                        ) ?? null
                      }
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <Group
        title="Pinned to this station"
        hint="Station-specific sets. They override the generic set for the same item on this bench only."
        sets={model.pinned}
        lookups={lookups}
        onPreview={setPreview}
        empty="No station-specific sets. The generic set for each item applies."
        pinned
      />
      <Group
        title="Generic sets that apply here"
        hint="Published for the item at any station — used here because nothing is pinned for that item."
        sets={model.generic}
        lookups={lookups}
        onPreview={setPreview}
        empty={
          itemIds.length === 0
            ? 'Link the items built here (Overview → Built here) to see their instructions.'
            : 'No generic sets for the items built here.'
        }
      />

      {creating && (
        <NewStationSetDialog
          station={station}
          lookups={lookups}
          onClose={() => setCreating(false)}
        />
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="h-[85vh] max-w-3xl p-0">
          <DialogTitle className="sr-only">Preview</DialogTitle>
          {preview && (
            <WorkInstructionsPanel instruction={preview} className="h-full rounded-2xl border-0" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Group({
  title,
  hint,
  sets,
  lookups,
  onPreview,
  empty,
  pinned,
}: {
  title: string;
  hint: string;
  sets: WorkInstructionWithSteps[];
  lookups: WorkLookups;
  onPreview: (set: WorkInstructionWithSteps) => void;
  empty: string;
  pinned?: boolean;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">
          {pinned && <Pin className="h-4 w-4 text-muted-foreground" />}
          {title}
          <span className="text-muted-foreground">({sets.length})</span>
        </h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </header>
      {sets.length === 0 ? (
        <div className="p-4">
          <QuietEmpty>{empty}</QuietEmpty>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {sets.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-medium text-foreground">{s.title}</p>
                  <Badge variant={s.is_published ? 'success-soft' : 'muted'}>
                    {s.is_published ? 'Live' : 'Draft'}
                  </Badge>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {lookups.itemName(s.item_id)}{' '}
                  <span className="opacity-70">{lookups.itemSku(s.item_id)}</span> ·{' '}
                  {s.steps.length} step{s.steps.length === 1 ? '' : 's'}
                  {s.target_seconds ? ` · target ${formatDuration(s.target_seconds)}` : ''} · v
                  {s.version} · updated {formatRelative(s.updated_at)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onPreview(s)}
                  disabled={s.steps.length === 0}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to={instructionEditorHref(s.id)}>
                    <Pencil className="h-4 w-4" />
                    Open in editor
                  </Link>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** One item built here without live instructions: jump to its draft, or start one pinned to the station. */
function MissingItemChip({
  itemId,
  lookups,
  station,
  canManage,
  draft,
}: {
  itemId: string;
  lookups: WorkLookups;
  station: FloorWorkstation;
  canManage: boolean;
  draft: WorkInstructionWithSteps | null;
}) {
  const navigate = useNavigate();
  const save = useSaveInstruction();
  const name = lookups.itemName(itemId) ?? 'Item';

  const start = () => {
    if (draft) {
      navigate(instructionEditorHref(draft.id));
      return;
    }
    save.mutate(
      { itemId, workstationId: station.id, title: `How to build ${name}`, isPublished: false },
      {
        onSuccess: (row) => navigate(instructionEditorHref(row.id)),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-3 pr-1 text-sm text-foreground">
      {name}
      {draft && <Badge variant="muted">Draft exists</Badge>}
      {canManage && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 rounded-full px-2.5 text-xs"
          onClick={start}
          disabled={save.isPending}
        >
          {draft ? 'Finish draft' : 'Write instructions'}
        </Button>
      )}
    </span>
  );
}

/** Start a draft pinned to this station and jump to the editor. */
function NewStationSetDialog({
  station,
  lookups,
  onClose,
}: {
  station: FloorWorkstation;
  lookups: WorkLookups;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const save = useSaveInstruction();
  const [itemId, setItemId] = useState<string | null>(station.default_item_id);
  const [title, setTitle] = useState(
    station.default_item_id
      ? `How to build ${lookups.itemName(station.default_item_id)} at ${station.name}`
      : '',
  );
  const [picking, setPicking] = useState(false);

  const pickItem = (id: string | null) => {
    setItemId(id);
    if (id) setTitle(`How to build ${lookups.itemName(id)} at ${station.name}`);
  };

  const create = () => {
    if (!itemId) {
      toast.error('Pick the item these instructions are for.');
      return;
    }
    save.mutate(
      { itemId, workstationId: station.id, title, isPublished: false },
      {
        onSuccess: (row) => {
          onClose();
          navigate(instructionEditorHref(row.id));
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New instruction set</DialogTitle>
          <DialogDescription>
            Pinned to {station.name}. It opens in the editor as a draft; nothing shows on the tablet
            until you publish.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Item</Label>
            <div className="flex items-center gap-2">
              <div className="flex min-h-10 flex-1 items-center rounded-lg border border-border bg-background px-3 text-sm">
                {itemId ? (
                  <span className="truncate">
                    {lookups.itemName(itemId)}{' '}
                    <span className="text-muted-foreground">{lookups.itemSku(itemId)}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Choose an item</span>
                )}
              </div>
              <Button variant="outline" onClick={() => setPicking(true)}>
                Choose
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="station-set-title">Title</Label>
            <Input
              id="station-set-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="How to build the Base"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={create} disabled={save.isPending || !title.trim() || !itemId}>
            Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
      <WorkItemPicker
        open={picking}
        onOpenChange={setPicking}
        stationId={station.id}
        currentItemId={itemId}
        onPick={pickItem}
        title="Which item are these instructions for?"
      />
    </Dialog>
  );
}
